import { useMemo } from "react";
import { Boxes, Lightbulb, TrendingUp } from "lucide-react";
import { SectionCard, Row, InfoNote } from "@/components/common/Cards";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { MARKET_VPS, MARKET_SHARED, evaluatePackage, balancedTiers } from "@/lib/engines/hosting";
import { fmtNum, fmtRp, fmtPct } from "@/lib/format";

export default function RecommendPanel({ basis, params, fleet }) {
  const vpsRows = useMemo(() => {
    return MARKET_VPS.map((t) => {
      const pkg = { ...t, type: "vps", ipv4: 1, dedicatedMbps: 0, licenseMonthly: 0, supportMonthly: 0, price: 0 };
      const res = evaluatePackage(pkg, basis, params);
      return { ...t, ...res };
    });
  }, [basis, params]);

  const sharedRows = useMemo(() => {
    return MARKET_SHARED.map((t) => {
      const pkg = { ...t, type: "shared", vcpu: 0, ramGb: 0, bandwidthTb: 0, ipv4: 0, licenseMonthly: 0, supportMonthly: 0, price: 0, diskUsagePct: t.diskUsagePct };
      const res = evaluatePackage(pkg, basis, params);
      return { ...t, ...res };
    });
  }, [basis, params]);

  const balanced = useMemo(() => balancedTiers(basis.cap), [basis]);
  const balancedRows = useMemo(() => {
    return balanced.map((t) => {
      const pkg = { ...t, type: "vps", ipv4: 1, dedicatedMbps: 0, licenseMonthly: 0, supportMonthly: 0, price: 0 };
      const res = evaluatePackage(pkg, basis, params);
      return { ...t, ...res };
    });
  }, [balanced, basis, params]);

  return (
    <div className="space-y-5">
      <InfoNote>
        Rekomendasi dihitung dari biaya satuan server Anda (<b>{basis.label}</b>). Gunakan tombol <b>Rekomendasi AI</b> di atas untuk analisa + strategi harga yang personal.
      </InfoNote>

      <SectionCard
        title="Paket VPS - tier standar pasar"
        eyebrow="Benchmark industri"
        description="Spesifikasi mengikuti vendor global (DigitalOcean/Vultr/Linode) agar mudah dibandingkan."
        bodyClassName="p-0"
        testid="hosting-market-vps-card"
      >
        <RecommendTable rows={vpsRows} />
      </SectionCard>

      <SectionCard
        title="Paket VPS - seimbang (sesuai hardware Anda)"
        eyebrow="Balanced"
        description="Rasio RAM/vCPU dan Disk/vCPU mengikuti rata-rata kapasitas server yang dipilih sehingga semua sumber daya habis seimbang."
        bodyClassName="p-0"
        testid="hosting-balanced-vps-card"
      >
        <RecommendTable rows={balancedRows} />
      </SectionCard>

      <SectionCard
        title="Paket Shared Hosting - tier standar pasar"
        eyebrow="Benchmark industri"
        description="Perkiraan akun/server didasarkan rasio pelanggan per vCPU & per GB RAM."
        bodyClassName="p-0"
        testid="hosting-market-shared-card"
      >
        <Table className="dense-table">
          <TableHeader>
            <TableRow className="bg-secondary">
              <TableHead>Paket</TableHead>
              <TableHead className="text-right">Disk / akun</TableHead>
              <TableHead className="text-right">COGS / akun</TableHead>
              <TableHead className="text-right">Harga saran</TableHead>
              <TableHead className="text-right">Akun / server</TableHead>
              <TableHead>Bottleneck</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sharedRows.map((r) => (
              <TableRow key={r.name}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="text-right num">{fmtNum(r.diskGb)} GB</TableCell>
                <TableCell className="text-right num">{fmtRp(r.cogs)}</TableCell>
                <TableCell className="text-right num text-primary">{fmtRp(r.suggested)}</TableCell>
                <TableCell className="text-right num">{fmtNum(r.usersAtTarget)}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="border-border text-[10px]">{r.bottleneck}</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SectionCard>
    </div>
  );
}

function RecommendTable({ rows }) {
  return (
    <Table className="dense-table">
      <TableHeader>
        <TableRow className="bg-secondary">
          <TableHead>Paket</TableHead>
          <TableHead className="text-right">vCPU</TableHead>
          <TableHead className="text-right">RAM</TableHead>
          <TableHead className="text-right">Disk</TableHead>
          <TableHead className="text-right">COGS/bln</TableHead>
          <TableHead className="text-right">Harga saran</TableHead>
          <TableHead className="text-right">Margin</TableHead>
          <TableHead className="text-right">Unit/server</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.name}>
            <TableCell className="font-medium">{r.name}</TableCell>
            <TableCell className="text-right num">{fmtNum(r.vcpu)}</TableCell>
            <TableCell className="text-right num">{fmtNum(r.ramGb)} GB</TableCell>
            <TableCell className="text-right num">{fmtNum(r.diskGb)} GB</TableCell>
            <TableCell className="text-right num">{fmtRp(r.cogs)}</TableCell>
            <TableCell className="text-right num text-primary">{fmtRp(r.suggested)}</TableCell>
            <TableCell className="text-right num text-muted-foreground">{fmtPct(r.marginActual)}</TableCell>
            <TableCell className="text-right num text-muted-foreground">{fmtNum(r.usersAtTarget)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
